/**
 * Cache Status Bar Component
 *
 * Displays progress of ES and GEX cache healing operations.
 * Shows animated progress bar when data is being loaded.
 * Also shows local loading state when fetching historical data.
 *
 * Enhanced to show detailed progress info:
 * - Data source (local, replay, api, live)
 * - Trade counts
 * - Current operation (fetching, processing, caching)
 * - Gap minutes (time behind real-time)
 */

import './CacheStatusBar.css';

/**
 * Format trade count with commas
 */
function formatNumber(num) {
  if (num === null || num === undefined) return null;
  return num.toLocaleString();
}

/**
 * Get a human-readable source label
 */
function getSourceLabel(source) {
  const labels = {
    'local': 'Local File',
    'api': 'API',
    'replay': 'Live Replay',
    'live': 'Live Stream',
    'local+replay': 'Local + Replay',
  };
  return labels[source] || source;
}

function CacheStatusBar({ status = null, loading = false, loadingMessage = 'Loading data...' }) {
  // status comes from App's single status poller (/api/cache/status)
  const serverHealing = status?.es?.status === 'healing' || status?.greeks?.status === 'healing';

  // Show bar if server is healing OR local loading is true
  const visible = serverHealing || loading;

  if (!visible) {
    return null;
  }

  // Determine what to display
  let displayMessage = loadingMessage;
  let displayProgress = null;
  let cacheType = '';
  let details = null;

  if (serverHealing && status) {
    const esHealing = status.es?.status === 'healing';
    const greeksHealing = status.greeks?.status === 'healing';
    const activeCache = esHealing ? status.es : (greeksHealing ? status.greeks : null);
    cacheType = esHealing ? 'ES' : (greeksHealing ? 'Greeks' : '');

    if (activeCache) {
      displayMessage = activeCache.message || 'Loading...';
      displayProgress = activeCache.progress;
      details = activeCache.details;
    }
  }

  // Build detail chips to display
  const detailChips = [];

  if (details) {
    // Source chip
    if (details.source) {
      detailChips.push({
        label: getSourceLabel(details.source),
        color: details.source === 'live' ? '#10b981' : '#4a9eff', // green for live, blue otherwise
      });
    }

    // Trades count chip
    if (details.trades_loaded > 0) {
      detailChips.push({
        label: `${formatNumber(details.trades_loaded)} trades`,
        color: '#8b5cf6',
      });
    }

    // Gap minutes chip (only show if > 0)
    if (details.gap_minutes !== null && details.gap_minutes > 0) {
      detailChips.push({
        label: `${details.gap_minutes.toFixed(0)}m behind`,
        color: '#f59e0b',
      });
    }

    // File name chip (shortened)
    if (details.file_name) {
      const shortName = details.file_name.length > 25
        ? '...' + details.file_name.slice(-22)
        : details.file_name;
      detailChips.push({
        label: shortName,
        color: '#64748b',
      });
    }

    // Mode chip for GEX
    if (details.mode) {
      detailChips.push({
        label: details.mode === 'incremental' ? 'Incremental' : 'Full',
        color: details.mode === 'incremental' ? '#10b981' : '#4a9eff',
      });
    }
  }

  return (
    <div className="csb">
      {/* Main message row */}
      <div className="csb-row">
        <div className="csb-msg">
          {cacheType && <span className="csb-type">{cacheType}: </span>}
          {displayMessage}
        </div>
        {displayProgress !== null && (
          <div className="csb-pct">{displayProgress}%</div>
        )}
      </div>

      {/* Detail chips row */}
      {detailChips.length > 0 && (
        <div className="csb-chips">
          {detailChips.map((chip, index) => (
            <div key={index} className="csb-chip" style={{ '--c': chip.color }}>
              {chip.label}
            </div>
          ))}
        </div>
      )}

      {/* Progress bar */}
      <div className="csb-track">
        <div
          className="csb-fill"
          style={{ width: displayProgress !== null ? `${displayProgress}%` : '100%' }}
        />
      </div>
    </div>
  );
}

export default CacheStatusBar;
